import { z } from 'zod';

/**
 * #328 Phase 2 — the two-way, MULTI-DESTINATION inter-ops information-sharing thread
 * (PERP §5.4 up/down · §6.7 the RFA chain; coordination-w4 council A.5–A.8, migration
 * 0135). The DATA shapes + request schemas; the operator-facing copy lives in
 * apps/web/src/interopsVocabulary.ts (pinned by its lock test).
 *
 * A thread is CONTEXT-ATTACHED (incident | assistance_request | standalone). A message
 * fans OUT to N recipients — in-app COP agencies (municipal EOC, First Nation, the PEOC)
 * and/or out-of-app registry destinations (ISC, Public Safety Canada, ministries). Each
 * recipient carries its own received / acknowledged (in-app) or sent (out-of-app) state.
 *
 * DOCTRINE (founder-ratified 2026-07-05): the FEDERAL leg is a "Request for assistance
 * (RFA)" recorded to Public Safety Canada — PERP §6.7.2. GOC is a caption inside the RFA
 * record, NEVER a target. "Request assistance" (kind request_for_assistance) toward
 * ministries/federal — the ministry decides deployment (§6.7.1). An acknowledgement is a
 * RECEIPT only (A.6) — it never commands, approves, or mutates the message or any source.
 */

/** A thread's CONTEXT. Kept in lockstep with valid_interops_context_types (0135). */
export const INTEROPS_CONTEXT_TYPES = ['incident', 'assistance_request', 'standalone'] as const;
export const InteropsContextTypeSchema = z.enum(INTEROPS_CONTEXT_TYPES);
export type InteropsContextType = z.infer<typeof InteropsContextTypeSchema>;

/** A message's KIND. Kept in lockstep with valid_interops_message_kinds (0135; +
 *  'recorded_response' from 0138, #567 — a pasted-back reply from an out-of-app leg,
 *  joined to the thread as a recorded document. App A #72 Scribe). */
export const INTEROPS_MESSAGE_KINDS = [
  'information',
  'request_for_assistance',
  'recorded_response',
] as const;
export const InteropsMessageKindSchema = z.enum(INTEROPS_MESSAGE_KINDS);
export type InteropsMessageKind = z.infer<typeof InteropsMessageKindSchema>;

/** A recipient's delivery channel. 'in_app' = a COP agency (no delivery_log row); the
 *  other three = an out-of-app leg (delivery_log_id set). */
export const INTEROPS_DELIVERY_CHANNELS = ['in_app', 'webhook', 'email', 'console_log'] as const;
export const InteropsDeliveryChannelSchema = z.enum(INTEROPS_DELIVERY_CHANNELS);
export type InteropsDeliveryChannel = z.infer<typeof InteropsDeliveryChannelSchema>;

/** A thread-list row's derived direction (A.2 — DERIVED from author vs viewer, never stored). */
export type InteropsDirection = 'inbound' | 'outbound';

/* ------------------------------------------------------------------ *
 * Request schemas (POST bodies)
 * ------------------------------------------------------------------ */

/** ONE recipient target: an in-app agency XOR an out-of-app registry destination. */
export const InteropsRecipientInputSchema = z
  .object({
    agency_id: z.string().uuid().optional(),
    destination_id: z.string().uuid().optional(),
  })
  .refine((r) => (r.agency_id ? 1 : 0) + (r.destination_id ? 1 : 0) === 1, {
    message: 'each recipient must set exactly one of agency_id | destination_id',
  });
export type InteropsRecipientInput = z.infer<typeof InteropsRecipientInputSchema>;

/** POST /api/interops/messages — send one message, fanned out to recipients. */
export const InteropsMessageCreateSchema = z.object({
  context_type: InteropsContextTypeSchema,
  /** Required for incident/assistance_request; MUST be null/omitted for standalone
   *  (enforced by the route). A uuid otherwise. */
  context_id: z.string().uuid().nullish(),
  kind: InteropsMessageKindSchema,
  body: z.string().trim().min(1).max(8000),
  in_reply_to: z.string().uuid().optional(),
  recipients: z.array(InteropsRecipientInputSchema).min(1).max(20),
  session_id: z.string().min(1),
});
export type InteropsMessageCreate = z.infer<typeof InteropsMessageCreateSchema>;

/** POST /api/interops/messages/:id/acknowledge — per-recipient receipt (idempotent). */
export const InteropsAcknowledgeSchema = z.object({
  /** The recipient row to acknowledge. When omitted the route acks the current agency's
   *  own recipient row for the message. */
  recipient_id: z.string().uuid().optional(),
  acknowledged_by: z.string().max(200).optional(),
  session_id: z.string().min(1),
});
export type InteropsAcknowledge = z.infer<typeof InteropsAcknowledgeSchema>;

/** POST /api/interops/messages/:id/record-response (#567, migration 0138) — the operator
 *  pastes back a reply received OUT OF APP (an RFA email reply from Public Safety Canada / a
 *  ministry) so the comms loop finishes in the COP (App A #72 Scribe). recipient_id MUST be an
 *  OUT-OF-APP leg of the message (a delivered destination row) — an in-app party replies
 *  itself. A record, never a send (no delivery fan-out); the original leg's "Sent" is unchanged. */
export const InteropsRecordResponseSchema = z.object({
  /** The out-of-app recipient leg (of the message in the URL) the response came from. */
  recipient_id: z.string().uuid(),
  /** The pasted reply text — rendered as a quoted document on the thread. */
  body: z.string().trim().min(1).max(8000),
  session_id: z.string().min(1),
});
export type InteropsRecordResponse = z.infer<typeof InteropsRecordResponseSchema>;

/* ------------------------------------------------------------------ *
 * Read-model views
 * ------------------------------------------------------------------ */

/** One recipient of a message, with its recorded delivery + receipt state. */
export interface InteropsRecipientView {
  id: string;
  /** In-app COP recipient (agency). Set XOR destination. */
  recipient_agency_id: string | null;
  recipient_agency_name: string | null;
  /** Out-of-app registry recipient (delivery_destinations). Set XOR agency. */
  recipient_destination_id: string | null;
  recipient_destination_name: string | null;
  recipient_destination_kind: string | null;
  delivery_channel: InteropsDeliveryChannel;
  /** The delivery_log row an out-of-app leg wrote (null for in-app recipients). */
  delivery_log_id: string | null;
  /** Out-of-app leg only: whether the delivery service reported success. NEVER a read/ack
   *  claim — a delivered webhook/email/console record is NOT proof it was read (R3, A.6). */
  delivered: boolean | null;
  /** Out-of-app leg failure captured on the delivery_log row (R3 honest state). */
  delivery_error: string | null;
  /** In-app recipient read/ack receipts (ISO-8601 UTC | null). Never set for out-of-app. */
  read_at: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
}

/** One message in a thread, with its fan-out recipients. */
export interface InteropsMessageView {
  id: string;
  context_type: InteropsContextType;
  context_id: string | null;
  kind: InteropsMessageKind;
  author_agency_id: string;
  author_agency_name: string | null;
  author_agency_kind: string | null;
  body: string;
  in_reply_to: string | null;
  created_at: string;
  recipients: InteropsRecipientView[];
  /** #567 — set only on a 'recorded_response' message: which out-of-app destination the
   *  pasted-back reply came from (the id + its resolved name for the "Response from …" chrome).
   *  NULL on every ordinary message. */
  recorded_source_destination_id: string | null;
  recorded_source_destination_name: string | null;
}

/** GET /api/interops/threads?context_type&context_id — the ordered message list. */
export interface InteropsThreadView {
  context_type: InteropsContextType;
  context_id: string | null;
  messages: InteropsMessageView[];
}

/** One normalized row in the Coordination "Information sharing" roll-up (R2). The Liaison's
 *  single pane unions three sources for the scoped incident: interops messages, assistance
 *  requests (inbound), and agency tasks (outbound). Direction is DERIVED (A.2), never stored. */
export interface InteropsRollupItem {
  source: 'interops_message' | 'assistance_request' | 'agency_task';
  id: string;
  direction: InteropsDirection;
  /** "From <originator>" (inbound) | "To <target>" (outbound) — A.2. */
  party_label: string;
  /** The operator-facing kind label ("Information" | "Request for assistance (RFA)" |
   *  "Assistance request" | "Task"). */
  kind_label: string;
  summary: string;
  at: string;
  /** interops_message rows carry their fan-out recipients (the recorded onward legs — R3). */
  recipients?: InteropsRecipientView[];
  /** assistance_request / agency_task lifecycle status, when applicable. */
  status?: string | null;
}

/** GET /api/interops/rollup?incident_id — the assembled "Information sharing" list. */
export interface InteropsRollupView {
  incident_id: string;
  items: InteropsRollupItem[];
}
